/** Virtual Timeline Player engine — pure logic, no React/DOM.
 *
 * VISTOERA_VIDEO_PRODUCTION_ROUGH_CUT_TIMELINE_PREVIEW_AND_FINAL_
 * ASSEMBLY_CARD_P0_V1 §7–§13.
 *
 * The Rough Cut preview must play WITHOUT ever pre-rendering a merged
 * `preview.mp4` — it logically plays Shot #1 → Shot #2 → ... → a
 * placeholder segment for any Shot with no generated video yet → the
 * next real Shot, mapping a single `globalTime` (0..totalDuration)
 * onto "which Shot, and how far into that Shot" every frame. This
 * module is exactly that mapping, deliberately factored out of the
 * Shot orchestration panel so it can be unit-tested without mounting
 * any `<video>` element or React Flow — the panel only ever calls
 * these functions from inside its own `requestAnimationFrame`
 * loop / click handlers.
 *
 * Total duration is FIXED to the Storyboard's own canonical
 * `total_duration_sec` (§10 "无论生成了多少个镜头，总时长不变") — every
 * segment's `durationSec` here is the Shot's own canonical
 * `duration_sec` from the Storyboard, NEVER the actual generated video
 * file's real (possibly slightly different) duration. A mismatch is
 * exposed via `detectShotDurationMismatch` for the caller to log as
 * `SHOT_DURATION_MISMATCH` — v1 deliberately does not auto-retime.
 */

export type TimelineSegmentKind = 'video' | 'placeholder'

export interface TimelineSegment {
  shotId: string
  order: number
  /** Seconds from the start of the Rough Cut timeline. */
  startSec: number
  endSec: number
  durationSec: number
  kind: TimelineSegmentKind
  /** Present only when `kind === 'video'`. */
  videoUrl?: string | null
  posterUrl?: string | null
  /** Best-effort placeholder still image, in priority order (§11):
   * video asset (handled by `kind==='video'` instead) → keyframe/
   * thumbnail still → Storyboard visual reference → dark placeholder
   * (`posterUrl` null = render the dark placeholder). */
  posterFallbackReason?: 'MISSING_VIDEO' | 'GENERATING' | 'READY_TO_GENERATE' | 'READY_TO_PREP' | 'FAILED' | 'VIDEO_LOAD_ERROR'
}

export interface ShotLikeForTimeline {
  shotId: string
  order: number
  durationSec: number
  /** `null`/`undefined` → this Shot has no ready video → placeholder segment. */
  videoUrl?: string | null
  posterUrl?: string | null
  /** Only meaningful when `videoUrl` is set — used by
   * `detectShotDurationMismatch`, never used to change segment length. */
  actualVideoDurationSec?: number | null
  placeholderReason?: TimelineSegment['posterFallbackReason']
}

/** Builds the fixed-length segment list from ordered Shots. Segment
 * order is `order` ASC (caller is responsible for having already
 * sorted/validated Shot order against the Storyboard) — this function
 * itself re-sorts defensively so a caller passing an unsorted array
 * still gets a correct timeline. */
export function buildTimelineSegments(shots: ShotLikeForTimeline[]): TimelineSegment[] {
  const ordered = [...shots].sort((a, b) => a.order - b.order)
  let cursor = 0
  const segments: TimelineSegment[] = []
  for (const shot of ordered) {
    const durationSec = Math.max(0, Number(shot.durationSec) || 0)
    const startSec = cursor
    const endSec = cursor + durationSec
    const hasVideo = !!shot.videoUrl
    segments.push({
      shotId: shot.shotId,
      order: shot.order,
      startSec,
      endSec,
      durationSec,
      kind: hasVideo ? 'video' : 'placeholder',
      videoUrl: hasVideo ? shot.videoUrl : undefined,
      posterUrl: shot.posterUrl ?? undefined,
      posterFallbackReason: hasVideo ? undefined : shot.placeholderReason ?? 'MISSING_VIDEO',
    })
    cursor = endSec
  }
  return segments
}

/** Runtime "this Shot's real video URL failed to actually decode in the
 * browser" downgrade (P0-1, `VISTOERA_VIDEO_PRODUCTION_FINAL_ASSEMBLY_
 * LIFECYCLE_ERROR_FALLBACK_AND_PERSISTENCE_CLOSURE_P0_V3` §三). Distinct
 * from `MISSING_VIDEO` (no URL was ever bound at all) — a
 * `<video onError>` fires only after `buildTimelineSegments` already
 * decided `kind: 'video'` from a genuinely-present `videoUrl`, so this
 * is a pure POST-PROCESS step applied by the caller (never folded into
 * `buildTimelineSegments` itself, which has no concept of "did this URL
 * actually decode").
 *
 * Segment `startSec`/`endSec`/`durationSec` are NEVER touched — the
 * whole point (§3.2 "绝对不能因为一个视频加载失败而中断整个 Rough Cut")
 * is that a broken Shot still occupies its own canonical timeline slot,
 * exactly like a never-generated one, so the RAF clock keeps advancing
 * straight through it into the next Shot with zero special-casing. */
export function applyBrokenVideoOverrides(segments: TimelineSegment[], brokenShotIds: ReadonlySet<string>): TimelineSegment[] {
  if (brokenShotIds.size === 0) return segments
  let changed = false
  const next = segments.map((seg) => {
    if (seg.kind !== 'video' || !brokenShotIds.has(seg.shotId)) return seg
    changed = true
    return {
      ...seg,
      kind: 'placeholder' as const,
      videoUrl: undefined,
      // Deliberately also clear `posterUrl`: it is very often derived
      // from the SAME underlying output asset as the broken video
      // (e.g. `videoGenTaskPosterUrl` hitting the same expired/missing
      // storage object), so it is highly likely to ALSO 404. If left
      // set, the JSX's `posterUrl` branch would render a raw broken
      // `<img>` icon instead of the intended "视频暂时无法播放" text —
      // which is exactly the "goes blank" failure mode §三 forbids.
      // Clearing it forces the dark text placeholder branch instead.
      posterUrl: undefined,
      posterFallbackReason: 'VIDEO_LOAD_ERROR' as const,
    }
  })
  return changed ? next : segments
}

/** Total Rough Cut duration — sum of every segment's fixed canonical
 * duration. Callers that already know the Storyboard's own
 * `total_duration_sec` should prefer that value directly (§10 — it is
 * the single source of truth even if per-shot durations were ever
 * edited independently); this helper exists for the pure segment-only
 * unit tests and as a sane fallback. */
export function computeTotalDurationSec(segments: TimelineSegment[]): number {
  if (segments.length === 0) return 0
  return segments[segments.length - 1].endSec
}

export function clampGlobalTimeSec(globalTimeSec: number, totalDurationSec: number): number {
  if (!Number.isFinite(globalTimeSec)) return 0
  if (globalTimeSec < 0) return 0
  if (globalTimeSec > totalDurationSec) return totalDurationSec
  return globalTimeSec
}

export interface ResolvedTimelinePosition {
  segment: TimelineSegment
  segmentIndex: number
  /** Seconds elapsed since this segment's own start — always within
   * `[0, segment.durationSec]`. */
  localTimeSec: number
}

/** Maps a single `globalTimeSec` to "which segment, and how far into
 * it" (§8 "currentGlobalTime/activeShotIndex/currentLocalShotTime").
 * Returns `null` only when there are no segments at all (empty
 * Storyboard) — every other input (including exactly `totalDuration`,
 * or a time inside a zero-length Shot) resolves to a real segment. */
export function resolveSegmentAtTime(
  segments: TimelineSegment[],
  globalTimeSec: number,
): ResolvedTimelinePosition | null {
  if (segments.length === 0) return null
  const total = computeTotalDurationSec(segments)
  const t = clampGlobalTimeSec(globalTimeSec, total)

  // Linear scan is intentional — v1 timelines are bounded (dozens of
  // Shots, not thousands); a binary search would be premature
  // optimization for no measurable benefit at this scale.
  for (let i = 0; i < segments.length; i += 1) {
    const seg = segments[i]
    const isLast = i === segments.length - 1
    if (t < seg.endSec || (isLast && t <= seg.endSec)) {
      // Zero-length segment (duration 0) — still resolvable, localTime is 0.
      const localTimeSec = seg.durationSec > 0 ? Math.min(seg.durationSec, Math.max(0, t - seg.startSec)) : 0
      return { segment: seg, segmentIndex: i, localTimeSec }
    }
  }
  const lastIndex = segments.length - 1
  return { segment: segments[lastIndex], segmentIndex: lastIndex, localTimeSec: segments[lastIndex].durationSec }
}

/** Click-to-seek (§12) — jump the Player to the START of a given Shot,
 * from either the thumbnail strip or the Shot List row. Returns the
 * `globalTimeSec` to feed back into the Player's own clock state;
 * `null` if the Shot isn't part of this timeline (defensive — should
 * not happen for any Shot actually rendered from the same Storyboard). */
export function seekToShotStart(segments: TimelineSegment[], shotId: string): number | null {
  const seg = segments.find((s) => s.shotId === shotId)
  return seg ? seg.startSec : null
}

export function isShotActiveAtTime(segments: TimelineSegment[], shotId: string, globalTimeSec: number): boolean {
  const resolved = resolveSegmentAtTime(segments, globalTimeSec)
  return !!resolved && resolved.segment.shotId === shotId
}

/** Overlay copy for Rough Cut placeholder segments — distinct from broken-video. */
export function resolveMissingSegmentOverlayCopy(args: {
  reason: TimelineSegment['posterFallbackReason'] | undefined
  shotIndexLabel: number | null
  durationSec: number
}): { title: string; subtitle: string; hint: string } {
  const idx = args.shotIndexLabel != null ? `分镜 #${args.shotIndexLabel}` : '分镜'
  const dur = Math.max(0, Math.round(args.durationSec || 0))
  const durationPart = dur > 0 ? `占位 ${dur}秒` : '占位预览'
  if (args.reason === 'VIDEO_LOAD_ERROR') {
    return {
      title: '视频暂时无法播放',
      subtitle: `${idx} · ${durationPart}`,
      hint: '此时间段为占位预览',
    }
  }
  if (args.reason === 'GENERATING') {
    return {
      title: '视频生成中',
      subtitle: `${idx} · ${durationPart}`,
      hint: '此时间段为占位预览',
    }
  }
  if (args.reason === 'FAILED') {
    return {
      title: '视频生成失败',
      subtitle: `${idx} · ${durationPart}`,
      hint: '此时间段为占位预览',
    }
  }
  return {
    title: '视频未生成',
    subtitle: `${idx} · ${durationPart}`,
    hint: '此时间段为占位预览',
  }
}

/** Advances the Player's clock by `deltaSec` (called every RAF tick
 * with the real measured delta, never a fixed frame-time assumption).
 * `loop=false` (default) stops exactly at the end, matching a Rough
 * Cut preview (Final Assembly is the "real, complete video" — the
 * Rough Cut previewing forever on loop would misrepresent an
 * incomplete cut as a finished one). Returns the next `globalTimeSec`
 * and whether playback should stop (reached the end, non-looping). */
export function advanceClock(
  currentGlobalTimeSec: number,
  deltaSec: number,
  totalDurationSec: number,
  options?: { loop?: boolean },
): { nextGlobalTimeSec: number; ended: boolean } {
  const loop = options?.loop ?? false
  if (totalDurationSec <= 0) return { nextGlobalTimeSec: 0, ended: true }
  const raw = currentGlobalTimeSec + Math.max(0, deltaSec)
  if (raw >= totalDurationSec) {
    if (loop) {
      const wrapped = totalDurationSec > 0 ? raw % totalDurationSec : 0
      return { nextGlobalTimeSec: wrapped, ended: false }
    }
    return { nextGlobalTimeSec: totalDurationSec, ended: true }
  }
  return { nextGlobalTimeSec: raw, ended: false }
}

/** Canonical-vs-actual duration divergence (§10) — logged as a
 * `SHOT_DURATION_MISMATCH` warning by the caller, never used here to
 * change the timeline itself (v1 has no auto-retiming). */
export function detectShotDurationMismatch(
  canonicalDurationSec: number,
  actualDurationSec: number | null | undefined,
  toleranceSec = 0.35,
): boolean {
  if (actualDurationSec == null || !Number.isFinite(actualDurationSec)) return false
  return Math.abs(actualDurationSec - canonicalDurationSec) > toleranceSec
}

/** One shared reusable `<video>` element strategy (§14 "never decode 12
 * simultaneous `<video>` elements") — given the CURRENTLY active
 * segment index, which segment (if any) should be pre-buffered next.
 * The node component owns exactly 2 `<video>` DOM elements (current +
 * preload-next) and swaps which one is "active" as playback crosses a
 * segment boundary; this helper only decides WHICH segment is "next"
 * to hand the idle element, it never touches the DOM itself. */
export function nextPreloadSegment(segments: TimelineSegment[], activeIndex: number): TimelineSegment | null {
  const next = segments[activeIndex + 1]
  if (!next || next.kind !== 'video') return null
  return next
}
